import { CourseLessonVm, LessonType } from '../models/LessonVm';
import { useEffect, useState } from 'react';

import { ApiPortfolio } from '../models/ApiPortfolio';
import { ApiPortfolioState } from '../models/ApiPortfolioState';
import Constants from '../constants';
import { CourseVm } from '../models/PathwayVm';
import { LessonAttempt } from '../models/LessonAttempt';
import LessonService from '../services/LessonService';
import { ModuleCertificateModel } from '../models/ModuleCertificateModel';
import ProgressStatusCode from '../models/ProgressStatusCode';
import SettingsService from '../services/SettingsService';
import { isEmpty } from 'lodash';
import { useCourseLessons } from './useCourseLessons';
import { useCourses } from './useCourses';
import { useLatestPortfolioModules } from './useLatestPortfolioModules';
import { useModuleProgress } from './useModuleProgress';
import { useStatuscode } from './useStatuscode';
import useUserModulePortfolio from './useUserModulePortfolio';

export function useCourse(userkey: number, coursekey: number) {
  const [course, setCourse] = useState<CourseVm>(null);

  const {
    liveVersion: courseLiveVersion,
    liveVersionMap,
    loading: courseLoading,
    error: loadCoursesError,
    refetchCourseInfo
  } = useCourses(coursekey);
  const {
    lessons: liveLessons,
    loading: lessonsLoading,
    error: loadCourseLessonsError
  } = useCourseLessons(coursekey, courseLiveVersion);

  const {
    loadingProgress,
    modulesProgress,
    progressError,
    refetchModulesProgress
  } = useModuleProgress(userkey);

  const { loadingLatestPortfolios, latestPortfolios, updatePortfolios } =
    useLatestPortfolioModules(userkey, coursekey, courseLiveVersion);

  const { loadPortfolios, loadingPortfolios } = useUserModulePortfolio();
  const { getStatusCode, getExpiredState } = useStatuscode();

  useEffect(() => {
    if (isEmpty(liveLessons) || isEmpty(liveVersionMap)) {
      return;
    }

    const lessons: CourseLessonVm[] = [];
    let lessonLocked = false;
    let lockedPosition = 0;
    const userModulesProgressData = modulesProgress?.filter(
      (d) => d.coursekey === coursekey
    );
    for (const lesson of liveLessons) {
      const {
        assets: moduleVersionAssets,
        modulekey,
        moduleversion
      } = lesson.moduleversion;
      const { assets: moduleAssets, title, description } = lesson.module;
      const lessonType = LessonService.convertToLessonType(
        moduleAssets.typeFlags
      );
      // Lesson required
      const { position, required } = liveVersionMap?.find(
        (m) => m.modulekey === modulekey
      );

      const currentModule = userModulesProgressData?.find(
        (d) => d.modulekey === modulekey
      );
      const currentPortfolio: ApiPortfolio = latestPortfolios?.find(
        (p) => p.modulekey === modulekey
      );
      const cert: boolean = !!moduleAssets?.certificate?.media;
      let progressStatus: ProgressStatusCode = 'N';
      let currentPortfolioAttempt: LessonAttempt;
      if (currentPortfolio) {
        const status = getStatusCode(currentPortfolio, lessonLocked);
        currentPortfolioAttempt = { ...currentPortfolio, status };
      }

      if (currentModule) {
        progressStatus = getCertificateStatusCode(
          currentModule,
          lessonType,
          lessonLocked
        );
      }
      // Lock / Unlock the lessons after the required lesson.
      // Earlier workflow: Atleast one of the latest published version attempts is completed.
      // New workflow: Status is getting from `api/certificate/module/list/user/${userkey}/progress`.
      // If the current lesson certificate is valid or 'completed/passed' then unlock lessons thereafter.
      if (
        !lessonLocked &&
        required &&
        ['E', 'N', 'S'].includes(progressStatus)
      ) {
        lessonLocked = true;
        lockedPosition = position + 1;
      }
      const newVersionAvailable =
        progressStatus !== 'N' &&
        currentPortfolioAttempt &&
        currentPortfolioAttempt.moduleversion !== moduleversion;

      // B2-1561. This is exceptional rule for assessment 'Retry' and 'Submit' states,
      // should be reset to 'Started' when new version is available.
      if (
        newVersionAvailable &&
        [
          ApiPortfolioState.AssessmentRetry,
          ApiPortfolioState.AssessmentMark,
          ApiPortfolioState.AssessmentSubmit
        ].includes(currentPortfolio.state)
      ) {
        currentPortfolioAttempt.status = 'S';
        currentPortfolioAttempt.state = ApiPortfolioState.AssessmentStart;
      }

      lessons.push({
        pathkey: coursekey,
        cert,
        required,
        lessonkey: modulekey,
        type: lessonType,
        lessonVersion: moduleversion,
        title,
        description,
        allowRestart: !moduleVersionAssets?.native?.singleAttemptPerVersion,
        completionThreshold: moduleVersionAssets?.native?.threshold,
        completionType: SettingsService.convertCompletionType(
          moduleVersionAssets?.native?.completionFlags
        ),
        displaySettings:
          SettingsService.convertLessonAssetToDisplaySettings(moduleAssets),
        moduleProgressStatus:
          // `position` ==> current lesson position.
          // `lockedPosition` ==> lessons should be locked after this position.
          lessonLocked && position >= lockedPosition ? 'L' : progressStatus,
        latestPortfolioAttempt: currentPortfolioAttempt,
        newVersionAvailable
      });
    }

    const recentLessons = getRecentLessons(userModulesProgressData, lessons);

    const course: CourseVm = {
      type: 'course',
      coursekey,
      lessons,
      recentLessons
    };
    setCourse(course);
  }, [liveLessons, liveVersionMap, modulesProgress, latestPortfolios]);

  const refetchProgress = async () => {
    await refetchCourseInfo();
    await refetchModulesProgress();
    await updatePortfolios();
  };

  function getCertificateStatusCode(
    portfolio: ModuleCertificateModel,
    lessonType: LessonType,
    lessonLocked: boolean
  ): ProgressStatusCode {
    let status: ProgressStatusCode = 'N';
    const expiredState = getExpiredState(portfolio.expires);
    if (lessonLocked) {
      status = 'L'; //* Locked
    } else if (expiredState !== null) {
      status = expiredState;
    } else if (portfolio.completed !== null) {
      status = ['quiz', 'assessment'].includes(lessonType) ? 'P' : 'C'; // `Passed / Completed`
    } else {
      status = 'S'; //* Started
    }
    return status;
  }

  function getRecentLessons(
    modules: ModuleCertificateModel[],
    lessons: CourseLessonVm[]
  ): CourseLessonVm[] {
    if (isEmpty(modules)) return [];

    const recentLessons: CourseLessonVm[] = [];

    // Sort modules and lessons by updated time
    const sortedModules = modules.sort(
      (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
    );

    const sortedLessons = lessons
      .filter((l) => l.latestPortfolioAttempt) // filter out lessons without attempts
      .sort(
        (a, b) =>
          new Date(b.latestPortfolioAttempt.modified).getTime() -
          new Date(a.latestPortfolioAttempt.modified).getTime()
      );

    // Combine lessons and modules into a single sorted array
    const updatedLessons = [
      ...sortedModules.map((m) => ({
        lessonkey: m.modulekey,
        updated: m.updated
      })),
      ...sortedLessons.map((l) => ({
        lessonkey: l.lessonkey,
        updated: l.latestPortfolioAttempt.modified
      }))
    ]
      .sort(
        (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
      )
      .filter(
        (lesson, index, self) =>
          index === self.findIndex((l) => l.lessonkey === lesson.lessonkey)
      )
      .slice(0, Constants.NumberOfRecentLessons);

    // Get the corresponding lesson objects
    for (let updated of updatedLessons) {
      const accessedLesson = lessons.find(
        (l) => l.lessonkey === updated.lessonkey
      );
      if (accessedLesson?.latestPortfolioAttempt) {
        recentLessons.push(accessedLesson);
      }
    }

    return recentLessons;
  }

  return {
    course,
    loading:
      courseLoading ||
      lessonsLoading ||
      loadingProgress ||
      loadingLatestPortfolios,
    error: !!loadCoursesError || !!loadCourseLessonsError,
    refetchProgress
  };
}
